/* eslint-disable camelcase */
import React, { Component } from "react";
import PropTypes from "prop-types";
import MaskedInput from "react-text-mask";
import { StateSelection } from "@components/common";
import { BANK_ACCOUNT_TYPES, MASK_VALUE } from "@constants";
import { StripeService, PaymentService } from "@services";
import utils from "@utils";
import Modal from "../Modal/Modal";
import Image from "../Image/Image";

import "./DirectDepositAccountModal.scss";

class DirectDepositAccountModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			isVerified: false,
			editMode: false,
			form: {
				account_holder_name: "",
				routing_number: "",
				account_number: "",
				account_number_confirm: "",
				account_holder_type: "1",
				id_number: "",
				dateOfBirth: "",
				tax_id: "",
				name: "",
				line1: "",
				city: "",
				state: "",
				postal_code: "",
				photoId: null,
				photoPreview: "",
				fullyAuthorized: false,
			},
			validations: {
				account_holder_name: false, // true = has error
				routing_number: false,
				account_number: false,
				account_number_confirm: false,
				account_holder_type: false,
				id_number: false,
				dateOfBirth: false,
				tax_id: false,
				name: false,
				line1: false,
				city: false,
				state: false,
				postal_code: false,
				photoId: false,
				fullyAuthorized: false,
			},
		};

		this.initialState = { ...this.state };
		this.bankAccountTypeOptions = Object.values(BANK_ACCOUNT_TYPES);
	}

	componentDidUpdate({ show: prevShow }) {
		const { show } = this.props;
		if (prevShow !== show && show) this.setDirectDepositInfo();
	}

	/** Sets direct deposit information based on the `data` prop passed to the modal */
	setDirectDepositInfo = () => {
		const { form } = this.state;
		const { data: depositAccount } = this.props;
		const newForm = { ...form };
		let isVerified = false;

		// Populate form
		if (depositAccount.BankAccount) {
			const {
				BankAccount: { RoutingNumber, AccountHolderType, AccountHolderName },
				LegalEntity,
			} = depositAccount;

			// if there is LegalEntity the account type is individual
			if (LegalEntity) {
				const {
					IsVerified,
					Address: { Line1, City, State, PostalCode },
					Birthday: { Day, Month, Year },
				} = LegalEntity;

				isVerified = IsVerified;
				const { USStates = [] } = this._stateSelection?.state || {};

				// Search for state name first, and state abbreviation later
				const state =
					USStates.find(
						item => item.label.toLowerCase().substr(5) === State.toLowerCase(),
					) ??
					USStates.find(
						item => item.label.toUpperCase().substr(0, 2) === State.toUpperCase(),
					) ??
					"";

				newForm.dateOfBirth = `${Month}/${Day}/${Year}`; // MM/DD/YYYY
				newForm.line1 = Line1;
				newForm.city = City;
				newForm.state = state ? state.value : "";
				newForm.postal_code = PostalCode;
			} else {
				// if there is no LegalEntity the account type is company and we assume it's approved
				isVerified = true;
			}

			newForm.routing_number = RoutingNumber;
			newForm.account_holder_name = AccountHolderName;
			newForm.account_holder_type = this.bankAccountTypeOptions.find(
				item => item.label.toLowerCase() === AccountHolderType,
			).value;
		}

		this.setState({
			show: true,
			form: newForm,
			isVerified,
			editMode: !!depositAccount.BankAccount,
		});
	};

	/**
	 * @async
	 * Validates that all necessary form details are present before submitting them to Stripe.
	 * This creates a payment method token when successful, which is saved/updated in our database.
	 */
	onSubmit = async () => {
		const { lang } = global;
		const { callback, isOrganizationDepositAccount, user } = this.props;
		const { isVerified, form, editMode, validations } = this.state;

		// Form validation
		const bankAccountType = parseInt(form.account_holder_type, 10);

		const nameArray = form.account_holder_name.split(" ");
		const validationErrors = { ...validations };

		validationErrors.account_holder_name =
			form.account_holder_name === "" || nameArray.length < 2;
		validationErrors.routing_number = form.routing_number === "";
		validationErrors.account_number = form.account_number === "";
		validationErrors.account_number_confirm =
			form.account_number_confirm === "" ||
			form.account_number !== form.account_number_confirm;
		validationErrors.fullyAuthorized =
			form.fullyAuthorized === "" || form.fullyAuthorized === false;

		if (!isVerified) {
			validationErrors.line1 = form.line1 === "";
			validationErrors.city = form.city === "";
			validationErrors.state = form.state === "";
			validationErrors.postal_code = form.postal_code === "";
			validationErrors.id_number =
				bankAccountType === BANK_ACCOUNT_TYPES.INDIVIDUAL.value && form.id_number === "";
			validationErrors.dateOfBirth =
				bankAccountType === BANK_ACCOUNT_TYPES.INDIVIDUAL.value && form.dateOfBirth === "";
			validationErrors.photoId =
				bankAccountType === BANK_ACCOUNT_TYPES.INDIVIDUAL.value && form.photoId === null;
			validationErrors.tax_id =
				bankAccountType === BANK_ACCOUNT_TYPES.COMPANY.value && form.tax_id === "";
			validationErrors.name =
				bankAccountType === BANK_ACCOUNT_TYPES.COMPANY.value && form.name === "";
		}

		// If any validation property is true, the form is not valid
		const validationError = Object.values(validationErrors).some(item => item);

		this.setState({ validations: validationErrors, loading: !validationError });

		if (validationError) return;

		let accountToken = {};

		if (!isVerified) {
			let imageToken = { id: null };
			if (form.account_holder_type === BANK_ACCOUNT_TYPES.INDIVIDUAL.value.toString()) {
				imageToken = await StripeService.createImageToken(form.photoId);

				if (imageToken.error) {
					this.setState({ loading: false });
					utils.notify({
						type: "error",
						message: imageToken.error.message,
					});
					return;
				}
			}

			const { USStates = [] } = this._stateSelection?.state || {};
			const stateName = USStates.find(
				item => item.value === parseInt(form.state, 10),
			).label.substr(5);

			accountToken = await StripeService.createAccountToken(
				{
					...form,
					first_name: nameArray[0],
					last_name: nameArray[nameArray.length - 1],
					state: stateName,
					account_holder_type: parseInt(form.account_holder_type, 10),
				},
				imageToken.id,
			);

			if (accountToken.error) {
				this.setState({ loading: false });
				utils.notify({
					type: "error",
					message: accountToken.error.message,
				});
				return;
			}
		}

		const bankAccountToken = await StripeService.createBankAccountToken(form);

		if (bankAccountToken.error) {
			this.setState({ loading: false });
			utils.notify({
				type: "error",
				message: bankAccountToken.error.message,
			});
			return;
		}

		const data = { BankAccount: { TokenId: bankAccountToken.token.id } };
		if (!isVerified) {
			data.AccountTokenId = accountToken.token.id;
		}

		if (isOrganizationDepositAccount) {
			const { Organization } = user;
			if (editMode) {
				const {
					data: { Id },
				} = this.props;
				PaymentService.updateOrganizationDepositAccount(Organization.Id, Id, data)
					.then(() => {
						utils.notify({
							type: "success",
							message: lang.t("Updated Organization Deposit Account successfully"),
						});

						if (callback) callback();
					})
					.catch(({ response }) => {
						this.setState({ loading: false });
						utils.notify({
							type: "error",
							message:
								response?.data?.Message ??
								lang.t("Error updating Organization Deposit Account"),
						});
					});
			} else {
				PaymentService.saveOrganizationDepositAccount(Organization.Id, data)
					.then(() => {
						utils.notify({
							type: "success",
							message: lang.t("Added Organization Deposit Account successfully"),
						});

						if (callback) callback();
					})
					.catch(({ response }) => {
						this.setState({ loading: false });
						utils.notify({
							type: "error",
							message:
								response?.data?.Message ??
								lang.t("Error adding Organization Deposit Account"),
						});
					});
			}
		} else {
			PaymentService.save({ data, editMode })
				.then(() => {
					utils.notify({
						type: "success",
						message: editMode
							? lang.t("Direct Deposit Account updated successfully")
							: lang.t("Direct Deposit Account created successfully"),
					});

					if (callback) callback();
				})
				.catch(({ response }) => {
					this.setState({ loading: false });
					utils.notify({
						type: "error",
						message: response?.data?.Message ?? lang.t("Error adding Payment Method"),
					});
				});
		}
	};

	resetState = () => {
		this.setState({ ...this.initialState });
	};

	/**
	 * Handles changes to the photo chosen by the user to add or update
	 * @param {Event} e The HTML event handled by this object
	 */
	onPhotoChange = e => {
		const { files } = e.target;

		if (files && files[0]) {
			const {
				form: { photoPreview },
			} = this.state;

			if (photoPreview) URL.revokeObjectURL(photoPreview);
			const newPhotoPreview = URL.createObjectURL(files[0]);
			const photoId = files[0];

			this.setState(prevState => ({
				form: {
					...prevState.form,
					photoPreview: newPhotoPreview,
					photoId,
				},
			}));
		}
	};

	onFormChange =
		(property, integer = false) =>
		event => {
			const value = event?.value ?? event?.target?.value ?? "";

			this.setState(({ form }) => ({
				form: {
					...form,
					[property]: integer ? value.replace(/\D/g, "") : value,
				},
			}));
		};

	/**
	 * Toggles the user's authorization to use the bank account they provided details for
	 * @param {Event} event The HTML event handled by this method
	 */
	updateAccountAuthorization = ({ target: { checked } }) => {
		this.setState(prevState => ({
			form: {
				...prevState.form,
				fullyAuthorized: checked,
			},
		}));
	};

	render() {
		const { lang } = global;
		const { form, validations, loading, editMode, isVerified } = this.state;
		const { show, onHide } = this.props;

		return (
			<Modal
				id="direct-deposit-modal"
				show={show}
				onHide={onHide}
				onExited={this.resetState}
			>
				<Modal.Header closeButton>
					<Modal.Title>{`${editMode ? lang.t("Edit") : lang.t("Add")} ${lang.t("Direct Deposit Account")}`}</Modal.Title>
				</Modal.Header>
				<Modal.Body id="payment-form">
					<div
						className={`row form-group ${validations.account_holder_name ? "has-error" : ""}`}
					>
						<div className="col-xs-12">
							<label htmlFor="account_holder_name">{lang.t("Account Holder")}</label>
							<input
								type="text"
								id="account_holder_name"
								value={form.account_holder_name}
								onChange={this.onFormChange("account_holder_name")}
								className="form-control"
							/>
						</div>
					</div>
					<div
						className={`row form-group ${validations.routing_number ? "has-error" : ""}`}
					>
						<div className="col-xs-12">
							<label htmlFor="routing_number">{lang.t("Routing Number")}</label>
							<input
								type="text"
								id="routing_number"
								value={form.routing_number}
								onChange={this.onFormChange("routing_number", true)}
								className="form-control"
								maxLength={9}
							/>
						</div>
					</div>
					<div className="row">
						<div
							className={`col-sm-6 col-xs-12 form-group${validations.account_number ? " has-error" : ""}`}
						>
							<div>
								<label htmlFor="account_number">{lang.t("Account Number")}</label>
							</div>
							<input
								type="text"
								id="account_number"
								value={form.account_number}
								onChange={this.onFormChange("account_number", true)}
								className="form-control"
							/>
						</div>
						<div
							className={`col-sm-6 col-xs-12 form-group${validations.account_number_confirm ? " has-error" : ""}`}
						>
							<div>
								<label htmlFor="account_number_confirm">
									{lang.t("Confirm Account Number")}
								</label>
							</div>
							<input
								type="text"
								id="account_number_confirm"
								value={form.account_number_confirm}
								onChange={this.onFormChange("account_number_confirm", true)}
								className="form-control"
							/>
						</div>
					</div>

					<If condition={!isVerified}>
						<div
							className={`row form-group ${validations.account_holder_type ? "has-error" : ""}`}
						>
							<div className="col-xs-12">
								<label htmlFor="account_holder_type">
									{lang.t("Account Type")}
								</label>
								<select
									type="text"
									id="account_holder_type"
									value={form.account_holder_type}
									onChange={this.onFormChange("account_holder_type")}
									className="form-control"
								>
									<For
										each="item"
										of={this.bankAccountTypeOptions}
									>
										<option
											key={`bank_account_type_${item.value}`}
											value={item.value}
										>
											{item.label}
										</option>
									</For>
								</select>
							</div>
						</div>
						<Choose>
							<When
								condition={
									form.account_holder_type ===
									BANK_ACCOUNT_TYPES.INDIVIDUAL.value.toString()
								}
							>
								<div className="row">
									<div
										className={`col-xs-12 col-sm-6 form-group${validations.id_number ? " has-error" : ""}`}
									>
										<label htmlFor="id_number">
											{lang.t("Social Security Number")}
										</label>
										<MaskedInput
											type="tel"
											id="id_number"
											className="form-control"
											value={form.id_number}
											mask={MASK_VALUE.ssnMask}
											onChange={this.onFormChange("id_number", true)}
										/>
									</div>
									<div
										className={`col-xs-12 col-sm-6 form-group${validations.dateOfBirth ? " has-error" : ""}`}
									>
										<label htmlFor="dateOfBirth">
											{lang.t("Date of Birth (MM/DD/YYYY)")}
										</label>
										<MaskedInput
											type="tel"
											id="dateOfBirth"
											className="form-control"
											value={form.dateOfBirth}
											mask={MASK_VALUE.dobMask}
											onChange={this.onFormChange("dateOfBirth")}
										/>
									</div>
								</div>
							</When>
							<When
								condition={
									form.account_holder_type ===
									BANK_ACCOUNT_TYPES.COMPANY.value.toString()
								}
							>
								<div className="row">
									<div
										className={`col-xs-12 col-sm-6 form-group${validations.tax_id ? " has-error" : ""}`}
									>
										<label htmlFor="tax_id">{lang.t("ELVN / Tax ID")}</label>
										<input
											type="tel"
											id="tax_id"
											className="form-control"
											value={form.tax_id}
											onChange={this.onFormChange("tax_id", true)}
										/>
									</div>
									<div
										className={`col-xs-12 col-sm-6 form-group${validations.name ? " has-error" : ""}`}
									>
										<label htmlFor="company-name">
											{lang.t("Company Name")}
										</label>
										<input
											type="text"
											id="company-name"
											className="form-control"
											value={form.name}
											onChange={this.onFormChange("name")}
										/>
									</div>
								</div>
							</When>
						</Choose>
						<div className={`row form-group${validations.line1 ? " has-error" : ""}`}>
							<div className="col-xs-12">
								<label htmlFor="line1">{lang.t("Billing Street Address")}</label>
								<input
									type="text"
									id="line1"
									className="form-control"
									value={form.line1}
									onChange={this.onFormChange("line1")}
								/>
							</div>
						</div>
						<div className={`row form-group${validations.city ? " has-error" : ""}`}>
							<div className="col-xs-12">
								<label htmlFor="account-city">{lang.t("City")}</label>
								<input
									type="text"
									id="account-city"
									className="form-control"
									value={form.city}
									onChange={this.onFormChange("city")}
								/>
							</div>
						</div>
						<div className="row">
							<div
								className={`col-xs-12 col-sm-6 form-group${validations.state ? " has-error" : ""}`}
							>
								<label htmlFor="account-state">{lang.t("State")}</label>
								<StateSelection
									ref={ref => {
										this._stateSelection = ref;
									}}
									name="account-state"
									id="account-state"
									value={form.state}
									onChange={this.onFormChange("state")}
								/>
							</div>
							<div
								className={`col-xs-12 col-sm-6 form-group${validations.postal_code ? " has-error" : ""}`}
							>
								<label htmlFor="postal_code">{lang.t("Zip Code")}</label>
								<input
									type="tel"
									id="postal_code"
									className="form-control"
									maxLength="5"
									value={form.postal_code}
									onChange={this.onFormChange("postal_code", true)}
								/>
							</div>
						</div>
						<If
							condition={
								form.account_holder_type ===
								BANK_ACCOUNT_TYPES.INDIVIDUAL.value.toString()
							}
						>
							<div className="row form-group">
								<div
									className={`col-xs-12${validations.photoId ? " has-error" : ""}`}
								>
									<label
										className="photo-id-label"
										htmlFor="photoId"
									>
										{lang.t("Photo ID (Evidence Quality)")}
									</label>
									<input
										type="file"
										name="photoId"
										id="photoId"
										accept=".jpeg,.jpg,.png,.tif,.tiff"
										onChange={this.onPhotoChange}
									/>
									<Image
										src={form.photoPreview}
										className="img-responsive photo-id-image"
										fallbackSrc="avatar"
									/>
								</div>
							</div>
						</If>
					</If>

					<div className={`form-group${validations.fullyAuthorized ? " has-error" : ""}`}>
						<div className="checkbox">
							<label htmlFor="fullyAuthorized">
								<input
									type="checkbox"
									id="fullyAuthorized"
									onChange={this.updateAccountAuthorization}
									checked={form.fullyAuthorized === true}
								/>
								{lang.t("I attest that I have full authorization to this account")}
							</label>
						</div>
					</div>

					<div className="row">
						<div
							className="col-xs-12"
							id="deposit-account-btn-container"
						>
							<button
								type="button"
								className="btn btn-link"
								onClick={onHide}
							>
								{lang.t("Cancel")}
							</button>
							<button
								type="button"
								disabled={loading}
								onClick={this.onSubmit}
								className="btn btn-primary"
							>
								{loading ? lang.t("Loading") : lang.t("Save")}
							</button>
						</div>
					</div>
				</Modal.Body>
			</Modal>
		);
	}
}

DirectDepositAccountModal.propTypes = {
	show: PropTypes.bool,
	data: PropTypes.object,
	onHide: PropTypes.func,
	callback: PropTypes.func,
	isOrganizationDepositAccount: PropTypes.bool,
	user: PropTypes.object,
};

DirectDepositAccountModal.defaultProps = {
	show: false,
	isOrganizationDepositAccount: false,
	data: {},
	user: {},
	onHide: () => {},
	callback: () => {},
};

export default DirectDepositAccountModal;
